/**
 * @krisspy-file
 * @type page
 * @name "JourneySelectPage"
 * @title "Výběr cesty"
 * @description "Hlavní rozcestník po přihlášení – uživatel volí mezi cestou zákazníka, zaměstnance nebo osobní cestou."
 * @routes ["/journey-select"]
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Briefcase, Heart, ArrowRight, LogOut, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface JourneyCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  phases: string[];
  gradient: string;
  iconBg: string;
  onClick: () => void;
}

const JourneyCard: React.FC<JourneyCardProps> = ({
  icon, title, description, phases, gradient, iconBg, onClick,
}) => (
  <button
    onClick={onClick}
    className="group relative bg-white rounded-2xl border border-gray-200 p-6 text-left shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
  >
    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${gradient}`} />
    <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center mb-4`}>
      {icon}
    </div>
    <h3 className="text-lg font-bold text-gray-900 mb-1.5">{title}</h3>
    <p className="text-sm text-gray-500 mb-4 leading-relaxed">{description}</p>
    <div className="flex flex-wrap gap-1.5 mb-4">
      {phases.map((phase, i) => (
        <span key={phase} className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-md">
          {i + 1}. {phase}
        </span>
      ))}
    </div>
    <div className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 group-hover:text-indigo-700">
      Začít mapovat
      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
    </div>
  </button>
);

const JourneySelectPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {/* Header */}
      <header className="border-b border-gray-200/80 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Journey Mapper</h1>
            <p className="text-xs text-gray-500">Vyberte typ cesty, kterou chcete mapovat</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 hidden sm:inline">{user?.email}</span>
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                title="Administrace"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Admin</span>
              </button>
            )}
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Odhlásit</span>
            </button>
          </div>
        </div>
      </header>

      {/* Cards grid */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Kterou cestu dnes mapujeme?
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto">
            Každá cesta má 5 fází a vlastní knihovnu touchpointů a nápadů. Změny se ukládají automaticky.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <JourneyCard
            icon={<Users className="w-6 h-6 text-indigo-600" />}
            title="Cesta zákazníka"
            description="Mapujte zkušenost zákazníka od prvního kontaktu po dlouhodobou věrnost."
            phases={['Povědomí', 'Zvažování', 'Nákup', 'Servis', 'Věrnost']}
            gradient="from-indigo-400 to-blue-500"
            iconBg="bg-indigo-100"
            onClick={() => navigate('/journey/customer')}
          />
          <JourneyCard
            icon={<Briefcase className="w-6 h-6 text-emerald-600" />}
            title="Cesta zaměstnance"
            description="Sledujte zkušenost zaměstnance od náboru po dlouhodobou angažovanost."
            phases={['Nábor', 'Zvažování', 'Nástup', 'Rozvoj', 'Angažovanost']}
            gradient="from-emerald-400 to-teal-500"
            iconBg="bg-emerald-100"
            onClick={() => navigate('/journey/employee')}
          />
          <JourneyCard
            icon={<Heart className="w-6 h-6 text-rose-600" />}
            title="Osobní cesta"
            description="Vytvořte mapu vztahu od prvního setkání po společný dlouhý život."
            phases={['Objev', 'Poznávání', 'Závazek', 'Péče', 'Věrnost']}
            gradient="from-rose-400 to-pink-500"
            iconBg="bg-rose-100"
            onClick={() => navigate('/journey/personal')}
          />
        </div>
      </main>
    </div>
  );
};

export default JourneySelectPage;
